import { getAddressesApi } from './api/addresses/slice';
import { getCalendarRecurrencesApi } from './api/calendarRecurrences/slice';
import { getClientApi } from './api/client/slice';
import { getDraftTasksApi } from './api/draftTasks/slice';
import { getSheetsApi } from './api/duckuments/sheets/slice';
import { getExamplesApi } from './api/examples/slice';
import { getHouseholdApi } from './api/household/slice';
import { getInvitesApi } from './api/invites/slice';
import { getLetterRecommendationsApi } from './api/letterRecommendations/slice';
import { getMinAppVersionApi } from './api/minAppVersion/slice';
import { getNotificationSettingsApi } from './api/notificationSettings/slice';
import { getOAuthApi } from './api/oauth/slice';
import { getPasswordApi } from './api/password/slice';
import { getPaymentsApi } from './api/payments/slice';
import { getProductFeedbackApi } from './api/productFeedback/slice';
import { getPushNotificationsApi } from './api/pushNotifications/slice';
import { getRecurrencesApi } from './api/recurrences/slice';
import { getTaskReviewsApi } from './api/reviews/slice';
import { getSecureNotesApi } from './api/secureNotes/slice';
import { getSuggestionsApi } from './api/suggestions/slice';
import { getSystemApi } from './api/system/slice';
import { getTasksApi } from './api/tasks/slice';
import { getTextSearchApi } from './api/textSearch/slice';
import { getWaitlistsApi } from './api/waitlists/slice';
import type { BaseApi } from './utils/types';

export const getApiReactHooks = (api: BaseApi) => {
  const {
    useAcknowledgeClientPromptMutation,
    useChangeEmailMutation,
    useClerkSSOLoginMutation,
    useCreateClientMutation,
    useCreateMCPSocialAuthMutation,
    useDeleteClientMutation,
    useEditProfileMutation,
    useFrictionlessSignupMutation,
    useFrictionlessSSOSignupMutation,
    useLazyRetrieveClientQuery,
    useLazyRetrieveClientStatusQuery,
    useLazyRetrieveMCPSocialAuthQuery,
    useLazyRetrievePlansQuery,
    useLazyRetrievePlanTiersQuery,
    useLazyRetrieveReferrerDetailsQuery,
    useLoginMutation,
    useReengagementActivateMutation,
    useRetrieveClientPromptsQuery,
    useRetrieveClientQuery,
    useRetrieveClientStatusQuery,
    useRetrievePlansQuery,
    useRetrievePlanTiersQuery,
    useUpdatePlanMutation,
  } = getClientApi(api);

  const tasksApi = getTasksApi(api);

  const {
    useAcceptComplexityPricingMutation,
    useCloseTaskMutation,
    useConfirmDoneMutation,
    useCreateCommentMutation,
    useCreateTaskMutation,
    useGetShareUrlMutation,
    useGiftTaskMutation,
    useLazyListCommentsQuery,
    useLazyRetrieveTaskQuery,
    useListCommentsQuery,
    useListTasksQuery,
    usePreviewTaskQuery,
    useRetrieveTaskQuery,
    useShareTaskMutation,
    useSkipTaskMutation,
    useSnoozeTaskMutation,
    useStartTaskMutation,
    useTriggerSingleChoiceOptionMutation,
    useUnshareTaskMutation,
    useUnsnoozeTaskMutation,
    useUpdateTaskMutation,
  } = tasksApi;

  const {
    useCreateDraftTaskMutation,
    useCreateTaskFromDraftTaskMutation,
    useDeleteDraftTaskMutation,
    useEditDraftTaskAfterFailureMutation,
    /** @deprecated We're moving away from timeslots */
    useGenerateDraftTaskTimeSlotsMutation,
    useLazyListDraftTasksQuery,
    useLazyRetrieveDraftTaskQuery,
    useListDraftTasksQuery,
    useRetrieveDraftTaskQuery,
    useSubmitDraftTaskIQAnswersMutation,
    useUpdateDraftTaskMutation,
  } = getDraftTasksApi(api);

  const { useCreateAddressMutation, useListAddressesQuery, useUpdateAddressMutation } = getAddressesApi(api);

  const { useLazyListExamplesQuery, useListExamplesQuery } = getExamplesApi(api);

  const { useChangePasswordMutation, useForgotPasswordMutation, useResetPasswordMutation } = getPasswordApi(api);

  const { useCreateTaskReviewMutation } = getTaskReviewsApi(api, tasksApi);

  const { useRetrieveOptionSheetQuery, useReviewOptionSheetMutation } = getSheetsApi(api);

  const {
    useCreateSecureNoteMutation,
    useDeleteSecureNoteMutation,
    useLazyListSecureNotesQuery,
    useLazyRetrieveSecureNoteContentQuery,
    useListSecureNotesQuery,
    useReplaceSecureNoteContentMutation,
    useRetrieveSecureNoteContentQuery,
  } = getSecureNotesApi(api);
  const { useRetrieveMinAppVersionQuery } = getMinAppVersionApi(api);

  const { useRetrieveNotificationSettingsQuery, useUpdateNotificationSettingsMutation } =
    getNotificationSettingsApi(api);

  const { useCreateGeneralFeedbackMutation } = getProductFeedbackApi(api);

  const { useDeactivateOAuthAccountMutation, useListOAuthAccountsQuery, useUpdateOAuthCodeMutation } = getOAuthApi(api);

  const {
    useCreateRecurrenceMutation,
    useDeleteRecurrenceMutation,
    useLazyListRecurrencesQuery,
    useLazyRetrieveRecurrenceQuery,
    useListRecurrencesQuery,
    usePredictRecurrenceMutation,
    useRetrieveRecurrenceQuery,
    useShareRecurrenceMutation,
    useUnshareRecurrenceMutation,
  } = getRecurrencesApi(api);

  const { useCreateWaitlistContactMutation, useRetrieveWaitlistStatusQuery } = getWaitlistsApi(api);

  const {
    useCreateInviteMutation,
    useExpireInviteMutation,
    useLazyRetrieveInviteQuery,
    useRetrieveInviteQuery,
    useRetrieveReferralsQuery,
  } = getInvitesApi(api);
  const {
    useBuyCreditsMutation,
    useCreateSetupIntentMutation,
    useDeleteCardMutation,
    useLazyListCardsQuery,
    useLazyListPurchasesQuery,
    useLazyRetrievePromotionDetailsQuery,
    useListCardsQuery,
    useSetupIntentConfirmedMutation,
    useUpdateCardMutation,
    useUpdateDefaultCardMutation,
  } = getPaymentsApi(api);

  const {
    useClaimLowCreditBonusMutation,
    useLazyRetrieveCreditsQuery,
    useLazyRetrieveFullLedgerReportQuery,
    useLazyRetrieveHouseholdQuery,
    useRemoveMemberFromHouseholdMutation,
    useRetrieveCreditsQuery,
    useRetrieveFullLedgerReportQuery,
    useRetrieveHouseholdQuery,
  } = getHouseholdApi(api);

  const { useLazyListTaskSearchResultsQuery } = getTextSearchApi(api);

  const { useRegisterForPushTokenMutation } = getPushNotificationsApi(api);

  const {
    useDismissAllCalendarRecurrencesMutation,
    useDismissCalendarRecurrenceMutation,
    useListCalendarRecurrencesQuery,
  } = getCalendarRecurrencesApi(api);

  const {
    useListLetterRecommendationsQuery,
    useMarkLetterRecommendationAsViewedMutation,
    useRetrieveLetterRecommendationQuery,
  } = getLetterRecommendationsApi(api);

  const { useLazyListSuggestionsQuery, useListSuggestionsQuery, useRetrieveSuggestionQuery } = getSuggestionsApi(api);

  const {
    useAddressAutocompleteQuery,
    useAddressDetailsQuery,
    useAuthenticateImageKitQuery,
    useLazyAddressAutocompleteQuery,
    useLazyAddressDetailsQuery,
    useLazyAuthenticateImageKitQuery,
  } = getSystemApi(api);

  return {
    useAcceptComplexityPricingMutation,
    useAcknowledgeClientPromptMutation,
    useAddressAutocompleteQuery,
    useAddressDetailsQuery,
    useAuthenticateImageKitQuery,
    useBuyCreditsMutation,
    useChangeEmailMutation,
    useChangePasswordMutation,
    useClaimLowCreditBonusMutation,
    useClerkSSOLoginMutation,
    useCloseTaskMutation,
    useConfirmDoneMutation,
    useCreateAddressMutation,
    useCreateClientMutation,
    useCreateCommentMutation,
    useCreateDraftTaskMutation,
    useCreateGeneralFeedbackMutation,
    useCreateInviteMutation,
    useCreateMCPSocialAuthMutation,
    useCreateRecurrenceMutation,
    useCreateSecureNoteMutation,
    useCreateSetupIntentMutation,
    useCreateTaskFromDraftTaskMutation,
    useCreateTaskMutation,
    useCreateTaskReviewMutation,
    useCreateWaitlistContactMutation,
    useDeactivateOAuthAccountMutation,
    useDeleteCardMutation,
    useDeleteClientMutation,
    useDeleteDraftTaskMutation,
    useDeleteRecurrenceMutation,
    useDeleteSecureNoteMutation,
    useDismissAllCalendarRecurrencesMutation,
    useDismissCalendarRecurrenceMutation,
    useEditDraftTaskAfterFailureMutation,
    useEditProfileMutation,
    useExpireInviteMutation,
    useForgotPasswordMutation,
    useFrictionlessSSOSignupMutation,
    useFrictionlessSignupMutation,
    useGenerateDraftTaskTimeSlotsMutation,
    useGetShareUrlMutation,
    useGiftTaskMutation,
    useLazyAddressAutocompleteQuery,
    useLazyAddressDetailsQuery,
    useLazyAuthenticateImageKitQuery,
    useLazyListCardsQuery,
    useLazyListCommentsQuery,
    useLazyListDraftTasksQuery,
    useLazyListExamplesQuery,
    useLazyListPurchasesQuery,
    useLazyListRecurrencesQuery,
    useLazyListSecureNotesQuery,
    useLazyListSuggestionsQuery,
    useLazyListTaskSearchResultsQuery,
    useLazyRetrieveClientQuery,
    useLazyRetrieveClientStatusQuery,
    useLazyRetrieveCreditsQuery,
    useLazyRetrieveDraftTaskQuery,
    useLazyRetrieveFullLedgerReportQuery,
    useLazyRetrieveHouseholdQuery,
    useLazyRetrieveInviteQuery,
    useLazyRetrieveMCPSocialAuthQuery,
    useLazyRetrievePlanTiersQuery,
    useLazyRetrievePlansQuery,
    useLazyRetrievePromotionDetailsQuery,
    useLazyRetrieveRecurrenceQuery,
    useLazyRetrieveReferrerDetailsQuery,
    useLazyRetrieveSecureNoteContentQuery,
    useLazyRetrieveTaskQuery,
    useListAddressesQuery,
    useListCalendarRecurrencesQuery,
    useListCardsQuery,
    useListCommentsQuery,

    useListDraftTasksQuery,
    useListExamplesQuery,
    useListLetterRecommendationsQuery,
    useListOAuthAccountsQuery,
    useListRecurrencesQuery,
    useListSecureNotesQuery,
    useListSuggestionsQuery,
    useListTasksQuery,
    useLoginMutation,
    useMarkLetterRecommendationAsViewedMutation,
    usePredictRecurrenceMutation,
    usePreviewTaskQuery,
    useReengagementActivateMutation,
    useRegisterForPushTokenMutation,
    useRemoveMemberFromHouseholdMutation,
    useReplaceSecureNoteContentMutation,
    useResetPasswordMutation,
    useRetrieveClientPromptsQuery,
    useRetrieveClientQuery,
    useRetrieveClientStatusQuery,
    useRetrieveCreditsQuery,
    useRetrieveDraftTaskQuery,
    useRetrieveFullLedgerReportQuery,
    useRetrieveHouseholdQuery,
    useRetrieveInviteQuery,
    useRetrieveLetterRecommendationQuery,
    useRetrieveMinAppVersionQuery,
    useRetrieveNotificationSettingsQuery,
    useRetrieveOptionSheetQuery,
    useRetrievePlanTiersQuery,
    useRetrievePlansQuery,
    useRetrieveRecurrenceQuery,
    useRetrieveReferralsQuery,
    useRetrieveSecureNoteContentQuery,
    useRetrieveSuggestionQuery,
    useRetrieveTaskQuery,
    useRetrieveWaitlistStatusQuery,
    useReviewOptionSheetMutation,
    useSetupIntentConfirmedMutation,
    useShareRecurrenceMutation,
    useShareTaskMutation,
    useSkipTaskMutation,
    useSnoozeTaskMutation,
    useStartTaskMutation,
    useSubmitDraftTaskIQAnswersMutation,
    useTriggerSingleChoiceOptionMutation,
    useUnshareRecurrenceMutation,
    useUnshareTaskMutation,
    useUnsnoozeTaskMutation,
    useUpdateAddressMutation,
    useUpdateCardMutation,
    useUpdateDefaultCardMutation,
    useUpdateDraftTaskMutation,
    useUpdateNotificationSettingsMutation,
    useUpdateOAuthCodeMutation,
    useUpdatePlanMutation,
    useUpdateTaskMutation,
  };
};

type ApiReactHookName = keyof ReturnType<typeof getApiReactHooks>;

export type ApiHook<T extends ApiReactHookName> = ReturnType<typeof getApiReactHooks>[T];

export type ApiHookReturn<T extends ApiReactHookName> = ReturnType<ApiHook<T>>;
